import { useCacheHelpers } from '../apollo/cache-helpers'
import {
  CreateCommentInput,
  CommentFragment,
  CommentFragmentDoc,
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useUpdateCommentMutation,
  ProjectFragment,
  ProjectFragmentDoc,
  ProjectVersionFragmentDoc,
  ProjectVersionFragment,
  UpdateCommentInput,
} from '../generated/graphql'

type CommentMutations = {
  isLoading: boolean

  createComment: (
    input: CreateCommentInput,
    projectId?: string,
    projectVersionId?: string,
  ) => Promise<CommentFragment>

  updateComment: (
    id: string,
    input: UpdateCommentInput,
  ) => Promise<CommentFragment>

  deleteComment: (
    commentId: string,
    projectId?: string,
    projectVersionId?: string,
  ) => Promise<boolean>
}

export function useComments(): CommentMutations {
  const [createComment, { loading: isCreateLoading }] =
    useCreateCommentMutation()
  const [updateComment, { loading: isUpdateLoading }] =
    useUpdateCommentMutation()
  const [deleteComment, { loading: isDeleteLoading }] =
    useDeleteCommentMutation()
  const { addItemToList, getFragment, removeItemFromList } = useCacheHelpers()

  return {
    isLoading: isCreateLoading || isUpdateLoading || isDeleteLoading,

    async createComment(
      input: CreateCommentInput,
      projectId?: string,
      projectVersionId?: string,
    ) {
      const result = await createComment({
        variables: {
          input,
          projectId,
          projectVersionId,
        },

        update: (_, mutationResult) => {
          const comment = mutationResult.data?.createComment
          if (!comment) {
            return
          }
          if (projectId) {
            const project = getFragment<ProjectFragment>(
              projectId,
              ProjectFragmentDoc,
            )
            addItemToList(CommentFragmentDoc, comment, project, 'comments')
          }
          if (projectVersionId) {
            const projectVersion = getFragment<ProjectVersionFragment>(
              projectVersionId,
              ProjectVersionFragmentDoc,
            )
            addItemToList(
              CommentFragmentDoc,
              comment,
              projectVersion,
              'comments',
            )
          }
        },
      })

      if (!result.data) {
        throw new Error('Comment creation failed')
      }

      return result.data.createComment
    },

    async updateComment(id: string, input: UpdateCommentInput) {
      const result = await updateComment({
        variables: { id, input },
      })

      if (!result.data) {
        throw new Error('Comment update failed')
      }

      return result.data.updateComment
    },

    async deleteComment(
      commentId: string,
      projectId?: string,
      projectVersionId?: string,
    ) {
      if (!projectId && !projectVersionId) {
        throw new Error(
          'Relation information missing when removing comment from relation.',
        )
      }
      const result = await deleteComment({
        variables: { id: commentId },

        update: () => {
          if (projectId) {
            const project = getFragment<ProjectFragment>(
              projectId,
              ProjectFragmentDoc,
            )
            removeItemFromList(
              CommentFragmentDoc,
              commentId,
              project,
              'comments',
            )
          }
          if (projectVersionId) {
            const projectVersion = getFragment<ProjectVersionFragment>(
              projectVersionId,
              ProjectVersionFragmentDoc,
            )
            removeItemFromList(
              CommentFragmentDoc,
              commentId,
              projectVersion,
              'comments',
            )
          }
        },

        optimisticResponse: {
          deleteComment: true,
        },
      })

      if (!result.data) {
        throw new Error('Comment deletion failed')
      }

      return result.data.deleteComment
    },
  }
}
